import React, { useEffect, useState } from "react"
import { graphql, useStaticQuery } from "gatsby"

import { generateConditionalClassName, isClientSide } from "@utils"

import ImageAboveFold from "@components/ImageAboveFold/ImageAboveFold"

import { PlatformHeroGraphicDIV } from "./PlatformHeroGraphic.styles"

enum PILLS {
  "Live Chat" = "Live Chat",
  "Shared Inbox" = "Shared Inbox",
  "Knowledge Base" = "Knowledge Base",
  "Beacon" = "Beacon",
  "Workflows" = "Workflows",
  "Reporting" = "Reporting",
  "Messages" = "Messages",
  "Customer Management" = "Customer Management",
}

export const PILLS_COLORS = {
  [PILLS["Live Chat"]]: {
    background: "green-200",
    text: "green-600",
  },
  [PILLS["Shared Inbox"]]: {
    background: "slate-200",
    text: "slate-700",
  },
  [PILLS["Knowledge Base"]]: {
    background: "orange-200",
    text: "orange-600",
  },
  [PILLS["Beacon"]]: {
    background: "clay-300",
    text: "clay-800",
  },
  [PILLS["Workflows"]]: {
    background: "yellow-200",
    text: "yellow-600",
  },
  [PILLS["Reporting"]]: {
    background: "purple-200",
    text: "purple-600",
  },
  [PILLS["Messages"]]: {
    background: "magenta-200",
    text: "magenta-600",
  },
  [PILLS["Customer Management"]]: {
    background: "red-200",
    text: "red-600",
  },
}

const PILLS_TEXT = [
  Object.keys(PILLS),
  Object.keys(PILLS),
]

const PlatformHeroGraphic = () => {
  const {
    inboxGraphic,
    docsGraphic,
    beaconGraphic,
  } = useStaticQuery(graphql`
    query PlatformHeroGraphicQuery {
      inboxGraphic: contentfulAsset(contentful_id:{eq:"2ngZ9qIKmPpkJ9Rhd1DZ6r"}) {
        imgixImage {
          gatsbyImageData(width: 1320, imgixParams: { dpr: 2 })
        }
      }
      docsGraphic: contentfulAsset(contentful_id:{eq:"dDpx3Az8g6xMxBT0JtIHG"}) {
        imgixImage {
          gatsbyImageData(width: 632, imgixParams: { dpr: 2 })
        }
      }
      beaconGraphic: contentfulAsset(contentful_id:{eq:"22HhBhtoaqtjl3qxjW3VET"}) {
        imgixImage {
          gatsbyImageData(width: 355, imgixParams: { dpr: 2 })
        }
      }
    }
  `)

  const [percentageScrolled, setPercentageScrolled] = useState<number>(0)

  useEffect(() => {
    if (!isClientSide()) return

    const PillsWrapperEl = document.querySelector(".PlatformHero .pills-wrapper")
    if (!PillsWrapperEl) return

    const handleScroll = () => {
      const PillsWrapperRect = PillsWrapperEl.getBoundingClientRect()

      if (PillsWrapperRect.y + PillsWrapperRect.height < 0) return

      const scrollStart = Math.max(PillsWrapperRect.y - window.innerHeight, 0)
      const scrollEnd = window.scrollY + PillsWrapperRect.y + PillsWrapperRect.height

      setPercentageScrolled(
        (window.scrollY - scrollStart) / (scrollEnd - scrollStart)
      )
    }

    window.addEventListener("scroll", handleScroll)

    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  return (
    <PlatformHeroGraphicDIV>
      <div className="pills-wrapper">
        {PILLS_TEXT.map((pillTextRow, rowIndex) => (
          <div
            className="pills-row"
            key={`row--${rowIndex}`}
            style={{
              translate: rowIndex === 0 ?
                `calc(max(200px, 25vw) * ${percentageScrolled * 0.6} * -1)` :
                `calc(max(200px, 25vw) * ${percentageScrolled * 0.6})`
            }}
          >
            {pillTextRow.map((pillText, pillIndex) => (
              <div
                className={generateConditionalClassName({
                  pill: true,
                  "type--headline-bold-6XL": true,
                  [`color--${PILLS_COLORS[pillText as PILLS].text}`]: true,
                  [`background-color--${PILLS_COLORS[pillText as PILLS].background}`]: true,
                })}
                key={`${pillText}--${pillIndex}`}
              >
                {pillText}
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="container">
        <div className="grid platform-image--grid">
          {inboxGraphic && (
            <div className="platform-image--inbox">
              <ImageAboveFold
                aspectRatio={16 / 9}
                alt="Help Scout Platform"
                imageData={inboxGraphic.imgixImage.gatsbyImageData}
              />
            </div>
          )}
          {docsGraphic && (
            <div className="platform-image--docs">
              <ImageAboveFold
                aspectRatio={16 / 9}
                alt="Help Scout Platform"
                imageData={docsGraphic.imgixImage.gatsbyImageData}
              />
            </div>
          )}
          {beaconGraphic && (
            <div className="platform-image--beacon">
              <ImageAboveFold
                aspectRatio={3 / 4}
                alt="Help Scout Platform"
                imageData={beaconGraphic.imgixImage.gatsbyImageData}
              />
            </div>
          )}
        </div>
      </div>
    </PlatformHeroGraphicDIV>
  )
}

export default PlatformHeroGraphic
